import { useEffect, useState } from 'react';

import { Flex } from '@revenuemarket/cds';
import { Colors, typographyStyle } from '@revenuemarket/design';
import { ArrowLeft } from '@revenuemarket/ui';
import { Input, Typography } from 'antd';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';

import { GroupTreeItem } from '~apis/custom-label';
import { FlattenTreeResult, flattenTree } from '~components/ui/HorizontalTreeTable/Table';
import { TreeNodeId, addNode, buildIdToNodeMap } from '~components/ui/HorizontalTreeTable/utils';
import useGetAlertBannerInfo from '~components/ui/InfoAlertBanner/useGetAlertBannerInfo';
import { useGroupLabelsTree } from '~hooks/queries/custom-labels/query';
import { useDemoAwareCompanyId } from '~hooks/useDemoAwareCompanyId';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { mixpanel } from '~pages/_app';
import { PAGE_NAME } from '~pages/clobe/label-management';

import GroupLabelTreeTable from './GroupLabelTreeTable';
import GroupSearchList from './GroupSearchList';
import AddNode from './common/component/AddNode';
import FoldToggleButton from './common/component/FoldButton';
import useGroupLabelTreeStore from './store/groupLabelTreeStore';
import { TreeData } from './types';

interface Props {
  activeItem?: FlattenTreeResult<GroupTreeItem>;
  setActiveItem: (item: FlattenTreeResult<GroupTreeItem>) => void;
}

const GroupLabelManage: React.FC<Props> = ({ activeItem, setActiveItem }) => {
  const [searchValue, setSearchValue] = useState('');
  const { groupLabelTree, setGroupLabelTree } = useGroupLabelTreeStore();
  const { companyId } = useDemoAwareCompanyId();
  const { data } = useGroupLabelsTree({ companyId });
  const groups = data?.Data?.data?.tree?.children;

  const [mode, setMode] = useState<'view' | 'search'>('view');
  const [foldedAll, setFoldedAll] = useState(false);

  const flattenedTree = flattenTree(groupLabelTree);

  const { bannerHeight } = useGetAlertBannerInfo();

  const handleAddNode = (parentId: TreeNodeId, idToNodeMap: any) => {
    const newNode = {
      key: `${parentId}-temp`,
      status: 'new',
      value: {
        id: `${parentId}-temp`,
        label: <AddNode title="항목" />,
      },
      children: [],
    };

    addNode(parentId, newNode, idToNodeMap);
    setGroupLabelTree([...groupLabelTree]);
  };

  useEffect(() => {
    if ((groups?.length ?? 0) === 0) {
      setGroupLabelTree([
        {
          key: 'new',
          status: 'new',
          virtual: true,
          children: [],
          value: {
            id: 'new',
            label: <AddNode title="항목" />,
          },
        } as unknown as TreeData<GroupTreeItem>,
      ]);
      return;
    }

    if (groups?.length) {
      const groupTarget = groups?.map((group) => {
        return {
          ...group,
          key: group.key + '',
        };
      }) as unknown as TreeData<GroupTreeItem>[];

      const idToNodeMap = buildIdToNodeMap(groupTarget);

      const flattenedTree = flattenTree(groupTarget);

      flattenedTree.forEach((node) => {
        const isLeaf = !node.children?.length && node.value?.name;
        const hasOneChildren =
          node.children[0]?.status !== 'new' &&
          !node.children.map((child) => child.status).includes('new') &&
          // 첫번째 children인지
          node.groupRowIndex === 0;

        if (isLeaf) {
          handleAddNode(node.key, idToNodeMap);
        }

        if (hasOneChildren && node.parentId) {
          handleAddNode(node.parentId, idToNodeMap);
        }
      });

      setGroupLabelTree([
        ...groupTarget,
        {
          key: 'new',
          status: 'new',
          virtual: true,
          children: [],
          value: {
            id: 'new',
            label: <AddNode title="항목" />,
          },
        } as unknown as TreeData<GroupTreeItem>,
      ]);
    }
  }, [groups]);

  return (
    <Flex
      direction="column"
      gap={10}
      tabIndex={0}
      style={{
        outline: 'none',
        minWidth: 'calc(100vw - 610px)',
        width: '100%',
      }}
    >
      <Typography.Text style={{ ...typographyStyle['14_600'], color: Colors.gray800 }}>
        그룹 분류
      </Typography.Text>
      <Flex align="center" justify="space-between">
        <Flex align="center" gap={8}>
          <Flex gap={4} align="center">
            {searchValue ? (
              <ArrowLeft
                onClick={() => {
                  setSearchValue('');
                  setMode?.('view');
                }}
                style={{
                  cursor: 'pointer',
                }}
              />
            ) : null}
            <Input.Search
              placeholder="그룹 이름 검색"
              value={searchValue}
              onChange={(e) => {
                const { value } = e.target;
                setSearchValue(value);
                mixpanel.track(MIXPANEL_EVENT.GLOBAL.SEARCH, {
                  page: PAGE_NAME,
                  type: 'group-label-management',
                  searchParam: value,
                });
                if (!value) {
                  setMode?.('view');
                }

                if (mode !== 'search' && value) {
                  setMode?.('search');
                }
              }}
            />
          </Flex>
        </Flex>
        <FoldToggleButton onToggleFold={setFoldedAll} />
      </Flex>
      <OverlayScrollbarsComponent
        style={{
          overflowX: 'scroll',
          height: `calc(100vh - ${bannerHeight + 212}px)`,
        }}
        id="horizontalTreeTableScrollWrapper"
      >
        {mode === 'view' ? (
          <GroupLabelTreeTable
            treeData={groupLabelTree}
            setMode={setMode}
            mode={mode}
            activeItem={activeItem}
            setActiveItem={setActiveItem}
            foldedAll={foldedAll}
          />
        ) : (
          <GroupSearchList
            onSelectFinish={(key) => {
              setMode('view');
              setSearchValue('');
              setTimeout(() => {
                const node = document.querySelector(`[data-id="${key}"]`);

                if (node) {
                  node.scrollIntoView({
                    block: 'center',
                  });
                }
              }, 100);
            }}
            disableActionOnInactiveItem={false}
            treeList={flattenedTree}
            searchValue={searchValue}
            setActiveItem={setActiveItem}
          />
        )}
      </OverlayScrollbarsComponent>
    </Flex>
  );
};

export default GroupLabelManage;
